import {
  ApiError,
  disconnectStripe,
  getStripeConnectionStatus,
  startStripeConnect,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { StripeConnectionStatusResponse } from '@jq4race/shared';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// ── Types ─────────────────────────────────────────────────────────────────────

/** CAP-10 · REQ-10.1: Four distinct UI states for the billing settings page. */
type PageState =
  | { kind: 'loading' }
  | { kind: 'not-connected' }
  | { kind: 'connected'; accountName?: string; livemode: boolean }
  | { kind: 'cancelled' } // ?error=cancelled (user denied at Stripe)
  | { kind: 'error'; code: string }; // ?error=<other>

// ── Props ─────────────────────────────────────────────────────────────────────

interface BillingPageProps {
  /**
   * Query parameters injected by the route after the OAuth redirect.
   * REQ-10.2: read from URL on initial load, not a separate API call.
   */
  connected?: string; // "true" when Stripe redirects back after successful connect
  error?: string; // error code from Stripe or our backend (cancelled, invalid_state, etc.)
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BillingPage({ connected, error }: BillingPageProps) {
  const { t } = useTranslation('billing');
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [connectingInProgress, setConnectingInProgress] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    // REQ-10.2: if the URL carries redirect params, resolve UI state from them
    // WITHOUT a separate API call for the redirect-triggered transition.
    if (error === 'cancelled') {
      setState({ kind: 'cancelled' });
      return;
    }
    if (error) {
      setState({ kind: 'error', code: error });
      return;
    }
    if (connected === 'true') {
      // After successful OAuth we still fetch status to get accountName.
      // This is the single API call after the redirect — per spec the query
      // param indicates success; the status call fills in accountName/livemode.
    }

    try {
      const status: StripeConnectionStatusResponse = await getStripeConnectionStatus();
      if (status.connected) {
        setState({ kind: 'connected', accountName: status.accountName, livemode: status.livemode });
      } else {
        setState({ kind: 'not-connected' });
      }
    } catch (e) {
      setState({ kind: 'not-connected' });
      setErrorBanner(translateApiError(e, t, { fallback: t('loadError') }));
    }
  }, [connected, error, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleConnect() {
    setConnectingInProgress(true);
    setErrorBanner(null);
    try {
      const authorizeUrl = await startStripeConnect();
      window.location.href = authorizeUrl;
    } catch (e) {
      setConnectingInProgress(false);
      setErrorBanner(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('connected.connectError') })
          : translateApiError(e, t, { fallback: t('error.unexpected') }),
      );
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    setErrorBanner(null);
    try {
      await disconnectStripe();
      setDisconnectDialogOpen(false);
      setState({ kind: 'not-connected' });
    } catch (e) {
      setDisconnectDialogOpen(false);
      setErrorBanner(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('error.disconnectError') })
          : translateApiError(e, t, { fallback: t('error.unexpected') }),
      );
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <div className="space-y-6">
      <p className="max-w-xl text-[14px] leading-relaxed text-jq-graphite">{t('intro')}</p>

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {state.kind === 'loading' && (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          )}

          {/* State 1 — Not connected */}
          {state.kind === 'not-connected' && (
            <NotConnectedPanel onConnect={handleConnect} connecting={connectingInProgress} />
          )}

          {/* State 2 — Connected */}
          {state.kind === 'connected' && (
            <ConnectedPanel
              accountName={state.accountName}
              livemode={state.livemode}
              onDisconnect={() => setDisconnectDialogOpen(true)}
            />
          )}

          {/* State 3 — Cancelled (user denied at Stripe) */}
          {state.kind === 'cancelled' && (
            <CancelledPanel onRetry={handleConnect} connecting={connectingInProgress} />
          )}

          {/* State 4 — Error (backend/Stripe error) */}
          {state.kind === 'error' && (
            <ErrorPanel
              code={state.code}
              onRetry={handleConnect}
              connecting={connectingInProgress}
            />
          )}
        </CardContent>
      </Card>

      {/* Disconnect confirmation dialog (REQ-10.3) */}
      <Dialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('disconnectDialog.title')}</DialogTitle>
            <DialogDescription>{t('disconnectDialog.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDisconnectDialogOpen(false)}
              disabled={disconnecting}
            >
              {t('disconnectDialog.cancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? t('disconnectDialog.disconnecting') : t('disconnectDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── State panels ──────────────────────────────────────────────────────────────

function NotConnectedPanel({
  onConnect,
  connecting,
}: {
  onConnect: () => void;
  connecting: boolean;
}) {
  const { t } = useTranslation('billing');
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[14px] font-medium text-jq-charcoal">{t('notConnected.title')}</p>
        <p className="mt-1 text-[13px] text-jq-graphite">{t('notConnected.description')}</p>
      </div>
      <Button size="sm" onClick={onConnect} disabled={connecting}>
        {connecting ? t('notConnected.redirecting') : t('notConnected.connect')}
      </Button>
    </div>
  );
}

function ConnectedPanel({
  accountName,
  livemode,
  onDisconnect,
}: {
  accountName?: string;
  livemode: boolean;
  onDisconnect: () => void;
}) {
  const { t } = useTranslation('billing');
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-jq-success" strokeWidth={1.75} />
        <p className="text-[14px] font-medium text-jq-charcoal">
          {accountName
            ? t('connected.titleWithAccount', { accountName })
            : t('connected.titleWithoutAccount')}
        </p>
        {!livemode && (
          <span className="rounded-full border border-jq-sand/70 bg-jq-surface-1 px-2 py-0.5 text-[11px] font-medium text-jq-graphite">
            {t('testMode')}
          </span>
        )}
      </div>
      <p className="text-[13px] text-jq-graphite">{t('connected.description')}</p>
      <Button variant="outline" size="sm" onClick={onDisconnect}>
        {t('connected.disconnect')}
      </Button>
    </div>
  );
}

function CancelledPanel({ onRetry, connecting }: { onRetry: () => void; connecting: boolean }) {
  const { t } = useTranslation('billing');
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[14px] font-medium text-jq-charcoal">{t('cancelled.title')}</p>
        <p className="mt-1 text-[13px] text-jq-graphite">{t('cancelled.description')}</p>
      </div>
      <Button size="sm" onClick={onRetry} disabled={connecting}>
        {connecting ? t('notConnected.redirecting') : t('notConnected.connect')}
      </Button>
    </div>
  );
}

function ErrorPanel({
  code,
  onRetry,
  connecting,
}: {
  code: string;
  onRetry: () => void;
  connecting: boolean;
}) {
  const { t } = useTranslation('billing');
  const message = resolveErrorMessage(t, code);
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-jq-danger" strokeWidth={1.75} />
        <div>
          <p className="text-[14px] font-medium text-jq-charcoal">{t('error.title')}</p>
          <p className="mt-1 text-[13px] text-jq-graphite">{message}</p>
        </div>
      </div>
      <Button size="sm" onClick={onRetry} disabled={connecting}>
        {connecting ? t('notConnected.redirecting') : t('error.retry')}
      </Button>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveErrorMessage(
  t: (key: string, opts?: Record<string, unknown>) => string,
  code: string,
): string {
  switch (code) {
    case 'invalid_state':
      return t('error.invalidState');
    case 'stripe_error':
      return t('error.stripeError');
    default:
      return t('error.unknown', { code });
  }
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('billing');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
